/**
 * Email Service - Gmail API Integration
 *
 * Handles email sending via Gmail API through Supabase Edge Function.
 * In development mode, simulates sending and stores emails locally.
 *
 * Setup: Set REACT_APP_GMAIL_MODE='live' and configure Supabase Edge Function
 * to enable real Gmail sending. Default mode is 'demo' (simulated).
 */

const GMAIL_MODE = process.env.REACT_APP_GMAIL_MODE || 'demo';
const SUPABASE_EDGE_URL = process.env.REACT_APP_SUPABASE_URL
  ? `${process.env.REACT_APP_SUPABASE_URL}/functions/v1/send-email`
  : null;

/**
 * Strip HTML tags from text (for plain text fallback)
 * @param {string} html - HTML string
 * @returns {string} Plain text with tags removed
 */
const stripHtmlTags = (html) => {
  if (!html) return '';
  return html
    .replace(/<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi, '')
    .replace(/<style\b[^<]*(?:(?!<\/style>)<[^<]*)*<\/style>/gi, '')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&quot;/g, '"')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .trim();
};

/**
 * Escape a string for safe interpolation into HTML.
 * Prevents a malicious client name or event name (e.g. "Mr <script>...") from
 * injecting markup into an outgoing HTML email.
 */
const escapeHtml = (value) => {
  if (value === null || value === undefined) return '';
  return String(value)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
};

/**
 * Replace template variables with actual event/client data.
 *
 * When the template is HTML (the default — all our email bodies are HTML),
 * variable values are HTML-escaped before substitution. Pass
 * { escape: false } to opt out for plain-text templates.
 *
 * @param {string} text - Template text with {VARIABLE} placeholders
 * @param {Object} variables - Key-value pairs for replacement
 * @param {Object} [opts] - { escape: boolean } (default true)
 * @returns {string} Text with variables replaced
 */
export const replaceVariables = (text, variables = {}, opts = {}) => {
  if (!text) return '';
  const shouldEscape = opts.escape !== false;
  let result = text;
  Object.entries(variables).forEach(([key, value]) => {
    const regex = new RegExp(`\\{${key}\\}`, 'g');
    const safe = shouldEscape ? escapeHtml(value) : (value == null ? '' : String(value));
    result = result.replace(regex, safe);
  });
  return result;
};

/**
 * Build variable map from event data
 * @param {Object} event - Event object
 * @param {Object} venue - Venue object (optional)
 * @returns {Object} Variable map for template replacement
 */
export const buildVariables = (event, venue = null) => {
  const depositAmount = event.depositPaid || Math.floor((event.packageTotal || 0) * 0.3);
  const eventDate = event.date
    ? new Date(event.date).toLocaleDateString('en-IE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })
    : 'TBD';

  return {
    CLIENT_NAME: event.client || '',
    CLIENT_FIRST_NAME: (event.client || '').split(' ')[0],
    CLIENT_EMAIL: event.clientEmail || event.email || '',
    EVENT_NAME: event.name || '',
    EVENT_DATE: eventDate,
    EVENT_TIME: event.startTime || 'TBD',
    EVENT_TYPE: event.type || 'event',
    VENUE_NAME: venue?.name || 'Errigal Country House Hotel',
    GUEST_COUNT: String(event.guests || 0),
    PACKAGE_TOTAL: `€${(event.packageTotal || 0).toLocaleString('en-IE', { minimumFractionDigits: 2 })}`,
    DEPOSIT_AMOUNT: `€${depositAmount.toLocaleString('en-IE', { minimumFractionDigits: 2 })}`,
    DEPOSIT_DATE: event.depositDate || 'as agreed',
    BALANCE_DUE: `€${((event.packageTotal || 0) - depositAmount).toLocaleString('en-IE', { minimumFractionDigits: 2 })}`,
    PAYMENT_TERMS: '14 days before the event',
    HOTEL_NAME: 'Errigal Country House Hotel',
    HOTEL_PHONE: '049 5556901',
    HOTEL_EMAIL: 'info@errigalhotel.com',
    HOTEL_ADDRESS: 'Cootehill, Co. Cavan, Ireland',
  };
};

/**
 * Available template variables for the editor
 */
export const TEMPLATE_VARIABLES = [
  { key: 'CLIENT_NAME', label: 'Client Name', example: 'Sarah & James McNally' },
  { key: 'CLIENT_FIRST_NAME', label: 'Client First Name', example: 'Sarah' },
  { key: 'CLIENT_EMAIL', label: 'Client Email', example: 'client@example.com' },
  { key: 'EVENT_NAME', label: 'Event Name', example: 'Wedding Reception' },
  { key: 'EVENT_DATE', label: 'Event Date', example: 'Saturday 20th June 2026' },
  { key: 'EVENT_TIME', label: 'Event Time', example: '18:00' },
  { key: 'EVENT_TYPE', label: 'Event Type', example: 'wedding' },
  { key: 'VENUE_NAME', label: 'Venue Name', example: 'The Full Skeffington Suite' },
  { key: 'GUEST_COUNT', label: 'Guest Count', example: '180' },
  { key: 'PACKAGE_TOTAL', label: 'Package Total', example: '€13,500.00' },
  { key: 'DEPOSIT_AMOUNT', label: 'Deposit Amount', example: '€6,750.00' },
  { key: 'DEPOSIT_DATE', label: 'Deposit Due Date', example: 'as agreed' },
  { key: 'BALANCE_DUE', label: 'Balance Due', example: '€6,750.00' },
  { key: 'PAYMENT_TERMS', label: 'Payment Terms', example: '14 days before the event' },
  { key: 'HOTEL_NAME', label: 'Hotel Name', example: 'Errigal Country House Hotel' },
  { key: 'HOTEL_PHONE', label: 'Hotel Phone', example: '049 5556901' },
  { key: 'HOTEL_EMAIL', label: 'Hotel Email', example: 'info@errigalhotel.com' },
];

/**
 * Generate HTML email with Errigal branding
 * @param {string} bodyHtml - The email body HTML content
 * @param {string} subject - Email subject (for preview text)
 * @returns {string} Complete branded HTML email
 */
export const generateBrandedEmail = (bodyHtml, subject = '') => {
  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>${subject}</title>
</head>
<body style="margin:0;padding:0;background-color:#f3f4f6;font-family:Georgia,'Times New Roman',serif;">
  <table width="100%" cellpadding="0" cellspacing="0" style="background-color:#f3f4f6;padding:32px 0;">
    <tr>
      <td align="center">
        <table width="600" cellpadding="0" cellspacing="0" style="background-color:#ffffff;border-radius:8px;overflow:hidden;box-shadow:0 2px 8px rgba(0,0,0,0.08);">
          <!-- Header -->
          <tr>
            <td style="background-color:#1A5C3A;padding:28px 40px;text-align:center;">
              <h1 style="color:#ffffff;font-size:22px;margin:0;font-weight:normal;letter-spacing:1px;">
                ERRIGAL COUNTRY HOUSE HOTEL
              </h1>
              <p style="color:#a7d5b8;font-size:12px;margin:6px 0 0;letter-spacing:2px;text-transform:uppercase;">
                Cootehill &bull; Co. Cavan &bull; Ireland
              </p>
            </td>
          </tr>
          <!-- Accent line -->
          <tr>
            <td style="background-color:#c8a96e;height:3px;"></td>
          </tr>
          <!-- Body -->
          <tr>
            <td style="padding:36px 40px;color:#333333;font-size:15px;line-height:1.7;">
              ${bodyHtml}
            </td>
          </tr>
          <!-- Footer -->
          <tr>
            <td style="background-color:#f9fafb;padding:24px 40px;border-top:1px solid #e5e7eb;">
              <table width="100%" cellpadding="0" cellspacing="0">
                <tr>
                  <td style="color:#6b7280;font-size:12px;line-height:1.5;">
                    <strong style="color:#1A5C3A;">Errigal Country House Hotel</strong><br>
                    Cootehill, Co. Cavan, Ireland<br>
                    <a href="tel:0495556901" style="color:#1A5C3A;text-decoration:none;">049 5556901</a> &bull;
                    <a href="mailto:info@errigalhotel.com" style="color:#1A5C3A;text-decoration:none;">info@errigalhotel.com</a>
                  </td>
                </tr>
              </table>
            </td>
          </tr>
        </table>
        <!-- Unsubscribe -->
        <p style="color:#9ca3af;font-size:11px;margin-top:16px;text-align:center;">
          You received this email because of your enquiry with Errigal Country House Hotel.
        </p>
      </td>
    </tr>
  </table>
</body>
</html>`;
};

/**
 * Send an email
 * In demo mode: simulates sending and returns a mock response
 * In live mode: calls Supabase Edge Function which sends via configured SMTP
 *
 * @param {Object} emailData - Email data
 * @param {string} emailData.to - Recipient email
 * @param {string} emailData.subject - Email subject
 * @param {string} emailData.bodyHtml - HTML body content
 * @param {string} emailData.bodyText - Plain text fallback
 * @param {string} emailData.replyTo - Reply-to address (optional)
 * @param {string[]} emailData.cc - CC addresses (optional)
 * @param {string[]} emailData.bcc - BCC addresses (optional)
 * @returns {Promise<Object>} Send result with id, status, timestamp
 */
export const sendEmail = async (emailData) => {
  const { to, subject, bodyHtml, bodyText, replyTo, cc, bcc, attachments } = emailData;

  if (GMAIL_MODE === 'live' && SUPABASE_EDGE_URL) {
    // Live mode - call Supabase Edge Function
    try {
      // Validate email address format
      if (!to || typeof to !== 'string' || !to.includes('@')) {
        throw new Error('Invalid recipient email address');
      }

      const brandedHtml = generateBrandedEmail(bodyHtml, subject);
      const plainText = bodyText || stripHtmlTags(brandedHtml);

      // Generate a per-email tracking token so opens and clicks can be tied
      // back to this specific sent_emails row. The send-email Edge Function
      // injects a tracking pixel and wraps <a href> links using this token.
      const trackingToken = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `trk_${Date.now()}_${Math.random().toString(36).slice(2, 10)}`;

      const response = await fetch(SUPABASE_EDGE_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${process.env.REACT_APP_SUPABASE_ANON_KEY}`,
        },
        body: JSON.stringify({
          to,
          subject,
          html: brandedHtml,
          text: plainText,
          replyTo: replyTo || 'info@errigalhotel.com',
          cc: (cc && Array.isArray(cc)) ? cc.filter(e => e) : [],
          bcc: (bcc && Array.isArray(bcc)) ? bcc.filter(e => e) : [],
          attachments: attachments || [],
          // Let the Edge Function insert the tracking pixel + wrap links.
          trackingToken,
        }),
      });

      if (!response.ok) {
        let errorMessage = 'Failed to send email';
        try {
          const error = await response.json();
          errorMessage = error.error || error.message || errorMessage;
        } catch {
          errorMessage = `HTTP ${response.status}: ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const result = await response.json();

      if (result.status !== 'sent') {
        throw new Error(result.error || 'Email send failed');
      }

      console.log('✅ Email sent via SMTP:', { to, subject, messageId: result.messageId });

      return {
        id: `email_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
        status: 'sent',
        timestamp: new Date().toISOString(),
        messageId: result.messageId,
        // Propagate the token so the caller can persist it on sent_emails
        // (column tracking_id). The track-email Edge Function looks it up
        // when a pixel loads or a link is clicked.
        trackingToken,
      };
    } catch (error) {
      console.error('❌ Email send failed:', error);
      return {
        id: `email_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
        status: 'failed',
        timestamp: new Date().toISOString(),
        error: error instanceof Error ? error.message : 'Unknown error',
      };
    }
  } else {
    // Demo mode - simulate sending
    const plainText = bodyText || stripHtmlTags(bodyHtml);
    console.log('📧 [DEMO] Email sent:', { to, subject });
    console.log('📧 [DEMO] Body preview:', plainText?.substring(0, 100));

    // Simulate network delay
    await new Promise((resolve) => setTimeout(resolve, 800));

    return {
      id: `demo_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`,
      status: 'sent',
      timestamp: new Date().toISOString(),
      demo: true,
    };
  }
};

/**
 * Convert a File object to a base64 attachment for sending via email
 * @param {File} file - File object from file input
 * @returns {Promise<Object>} Attachment object { filename, content, contentType }
 */
export const fileToEmailAttachment = (file) => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result.split(',')[1]; // Remove data:mime;base64, prefix
      resolve({
        filename: file.name,
        content: base64,
        contentType: file.type || 'application/octet-stream',
      });
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

/**
 * Check if Gmail integration is configured
 * @returns {Object} Connection status
 */
export const getGmailStatus = () => {
  if (GMAIL_MODE === 'live' && SUPABASE_EDGE_URL) {
    return { connected: true, mode: 'live', label: 'Gmail Connected' };
  }
  return { connected: false, mode: 'demo', label: 'Demo Mode' };
};

/**
 * Fetch inbound emails from info@errigalhotel.com
 * Live mode: calls Supabase Edge Function → IMAP → stores in inbound_emails table
 * Demo mode: returns simulated sample emails (when SMTP not configured)
 *
 * @param {Object} options
 * @param {number} options.maxResults - Max emails to return (default: 20)
 * @param {boolean} options.refresh - If true, triggers IMAP fetch; if false, reads from DB only
 * @returns {Promise<Object[]>} Array of email objects
 */
export const fetchInboundEmails = async (options = {}) => {
  const { maxResults = 20, refresh = true } = options;

  if (GMAIL_MODE === 'live') {
    try {
      const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
      const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

      if (!supabaseUrl || !supabaseKey) {
        throw new Error('Supabase not configured');
      }

      if (refresh) {
        // Trigger the Edge Function to fetch new emails via IMAP
        const fetchUrl = `${supabaseUrl}/functions/v1/fetch-emails`;
        try {
          const response = await fetch(fetchUrl, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${supabaseKey}`,
            },
            body: JSON.stringify({ maxResults }),
          });

          if (response.ok) {
            const data = await response.json();
            // Edge Function returns emails directly from DB after syncing
            if (data.emails && data.emails.length > 0) {
              return data.emails.map(dbEmailToApp);
            }
          } else {
            console.warn('fetch-emails Edge Function returned', response.status);
          }
        } catch (fetchErr) {
          console.warn('Edge Function call failed, reading from DB:', fetchErr.message);
        }
      }

      // Fallback: read directly from inbound_emails table
      const { createClient } = await import('@supabase/supabase-js');
      const supabase = createClient(supabaseUrl, supabaseKey);
      const { data, error } = await supabase
        .from('inbound_emails')
        .select('*')
        .order('date', { ascending: false })
        .limit(maxResults);

      if (!error && data && data.length > 0) {
        return data.map(dbEmailToApp);
      }

      // If DB is empty and we're in live mode, fall through to demo
      if (data && data.length === 0 && !refresh) {
        return []; // No emails yet, don't show demo data
      }
    } catch (error) {
      console.error('Inbound email fetch failed:', error.message);
      // Fall through to demo mode
    }
  }

  // Demo mode - return simulated inbound emails
  await new Promise((resolve) => setTimeout(resolve, 400));

  return [
    {
      id: 'demo_in_001',
      from: 'sarah.obrien@gmail.com',
      fromName: "Sarah O'Brien",
      subject: 'Wedding Enquiry - Summer 2027',
      body: "Hi,\n\nMy fiancé and I are looking at venues for our wedding in July 2027. We'd love to come see Errigal Country House Hotel. We're expecting around 180 guests.\n\nCould you let us know availability and pricing?\n\nThanks,\nSarah",
      date: new Date(Date.now() - 2 * 60 * 60 * 1000).toISOString(),
      snippet: "My fiancé and I are looking at venues for our wedding in July 2027...",
      labels: ['INBOX', 'UNREAD'],
      isRead: false,
    },
    {
      id: 'demo_in_002',
      from: 'mark.kelly@techstart.ie',
      fromName: 'Mark Kelly',
      subject: 'Corporate Dinner - 20 guests - May',
      body: "Good afternoon,\n\nWe're organising a team dinner for 20 people sometime in May. Would you have a private room available? We'd like a set menu with wine.\n\nRegards,\nMark Kelly\nTechStart Ltd",
      date: new Date(Date.now() - 8 * 60 * 60 * 1000).toISOString(),
      snippet: "We're organising a team dinner for 20 people sometime in May...",
      labels: ['INBOX'],
      isRead: true,
    },
    {
      id: 'demo_in_003',
      from: 'patricia.murphy@eircom.net',
      fromName: 'Patricia Murphy',
      subject: '60th Birthday Party Enquiry',
      body: "Hello,\n\nI'm planning a surprise 60th birthday party for my husband in September. We'd need space for about 50 people with a buffet.\n\nWhat dates do you have available and what are the costs?\n\nBest regards,\nPatricia Murphy\n087 654 3210",
      date: new Date(Date.now() - 24 * 60 * 60 * 1000).toISOString(),
      snippet: "I'm planning a surprise 60th birthday party for my husband...",
      labels: ['INBOX'],
      isRead: true,
    },
  ];
};

/**
 * Convert DB inbound_email row to the format the Dashboard expects
 */
function dbEmailToApp(row) {
  return {
    id: row.id,
    messageId: row.message_id,
    from: row.from_address,
    fromName: row.from_name || row.from_address?.split('@')[0] || '',
    subject: row.subject || '(No subject)',
    body: row.body_text || '',
    bodyHtml: row.body_html || '',
    snippet: row.snippet || row.body_text?.slice(0, 120) || '',
    date: row.date,
    labels: row.labels || ['INBOX'],
    isRead: row.is_read || false,
    isProcessed: row.is_processed || false,
    processedEventId: row.processed_event_id,
    hasAttachments: row.has_attachments || false,
    attachmentNames: row.attachment_names || [],
  };
}

/**
 * Delete an inbound email from the inbound_emails table
 * @param {string} emailId - The email ID to delete
 * @returns {Promise<Object>} Result with success status
 */
export const deleteInboundEmail = async (emailId) => {
  try {
    const { createClient } = await import('@supabase/supabase-js');
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseKey) {
      throw new Error('Supabase not configured');
    }

    const supabase = createClient(supabaseUrl, supabaseKey);
    const { error } = await supabase
      .from('inbound_emails')
      .delete()
      .eq('id', emailId);

    if (error) {
      console.error('Failed to delete email:', error.message);
      return { success: false, error: error.message };
    }

    console.log('✅ Email deleted:', emailId);
    return { success: true };
  } catch (error) {
    console.error('Delete email error:', error.message);
    return { success: false, error: error.message };
  }
};

/**
 * Add a manually entered inbound email to the inbound_emails table
 * @param {Object} emailData - Email data with from_name, from_address, subject, body, date
 * @returns {Promise<Object>} Result with the new email data
 */
export const addManualInboundEmail = async (emailData) => {
  try {
    const { createClient } = await import('@supabase/supabase-js');
    const supabaseUrl = process.env.REACT_APP_SUPABASE_URL;
    const supabaseKey = process.env.REACT_APP_SUPABASE_ANON_KEY;

    if (!supabaseUrl || !supabaseKey) {
      throw new Error('Supabase not configured');
    }

    const supabase = createClient(supabaseUrl, supabaseKey);

    // Generate unique message_id
    const messageId = `manual-${Date.now()}`;

    // Insert the new email record
    const { data, error } = await supabase
      .from('inbound_emails')
      .insert([
        {
          message_id: messageId,
          from_name: emailData.fromName || '',
          from_address: emailData.fromAddress || '',
          to_address: 'info@errigalhotel.com',
          subject: emailData.subject || '(No subject)',
          body_text: emailData.body || '',
          date: emailData.date || new Date().toISOString(),
          is_read: false,
          is_processed: false,
          labels: ['INBOX', 'MANUAL'],
          has_attachments: false,
          snippet: (emailData.body || '').substring(0, 120),
        },
      ])
      .select();

    if (error) {
      console.error('Failed to add manual email:', error.message);
      return { success: false, error: error.message };
    }

    if (!data || data.length === 0) {
      return { success: false, error: 'No data returned from insert' };
    }

    console.log('✅ Manual email added:', messageId);
    return { success: true, email: dbEmailToApp(data[0]) };
  } catch (error) {
    console.error('Add manual email error:', error.message);
    return { success: false, error: error.message };
  }
};
